import React, { useState, useEffect } from "react";
import {
  collection,
  doc,
  onSnapshot,
  getDoc,
  getDocs,
  updateDoc,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebase/firebase";
import type { TabType } from "../components/CheckInCheckOut/CheckInMain";
import { useColorMode, Skeleton } from "@chakra-ui/react";
import { getThemeClasses } from "../utils/themeUtils";
import { getTimesheetPermissions } from "../utils/permissions";
import { formatDateDDMMYYYY } from "../utils/dateUtils";
import {
  addDays,
  format,
  isAfter,
  startOfWeek,
  subWeeks,
} from "date-fns";
import CustomDropdown from "../components/CustomDropdown";
import { useCallback, useMemo } from "react";

export type TimesheetEntry = {
  id: string; // index in the entries array
  userId: string;
  date: string;
  project: string;
  jobCode: string;
  hours: number;
  status: "pending" | "approved" | "rejected";
  managerIds: string[];
  taskDescription: string;
  timestamp: string;
  rejectionReason?: string;
};

export type User = {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
  permissions?: "Admin" | "Editor" | "Viewer";
  approvalStatus?: "Yes" | "No";
};

interface TimeSheetApprovalPageProps {
  bg: string;
  color: string;
  organizationId: string;
  currentUserId: string;
  users: User[];
  setActiveTab?: React.Dispatch<React.SetStateAction<TabType>>;
  activeSubTab?: "timesheets" | "leaves" | "wfh" | "noncheckin";
  setActiveSubTab?: React.Dispatch<React.SetStateAction<"timesheets" | "leaves" | "wfh" | "noncheckin">>;
}

// Removed PAGE_SIZE as pagination was replaced by scrollable view

const TimeSheetApprovalPage: React.FC<TimeSheetApprovalPageProps> = ({
  bg,
  color,
  organizationId,
  currentUserId,
  users,
  setActiveTab,
  activeSubTab,
  setActiveSubTab,
}) => {
  const [loading, setLoading] = useState(true);
  const [entriesByUser, setEntriesByUser] = useState<
    Record<string, TimesheetEntry[]>
  >({});
  const [selectedUser, setSelectedUser] = useState<string>("ALL");
  // Removed page and userPages for scrollable view
  const [notification, setNotification] = useState<string | null>(null);
  const [notificationType, setNotificationType] = useState<
    "success" | "error" | null
  >(null);
  const [search, setSearch] = useState("");
  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    title: string;
    description?: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => Promise<void> | void;
  } | null>(null);
  // Searchable user dropdown state
  const [isUserPickerOpen, setIsUserPickerOpen] = useState(false);
  const [userQuery, setUserQuery] = useState("");
  const [userPickerPlacement, setUserPickerPlacement] = useState<"top" | "bottom">("bottom");
  const userPickerRef = React.useRef<HTMLDivElement | null>(null);
  const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null);

  /* ------------------- Inline Comments State ------------------- */
  const [rowComments, setRowComments] = useState<Record<string, string>>({});

  /* ------------------- Weekly Filtering State ------------------- */
  const [selectedWeekStartStr, setSelectedWeekStartStr] = useState<string>("ALL");

  const selectedWeekStart = useMemo(() => {
    return selectedWeekStartStr === "ALL" ? null : new Date(selectedWeekStartStr);
  }, [selectedWeekStartStr]);

  const generateWeekOptions = useCallback(() => {
    const options: { value: string; label: string }[] = [
      { value: "ALL", label: "All Pending Timesheets" }
    ];
    let current = startOfWeek(new Date(), { weekStartsOn: 1 });
    const startLimit = new Date(2025, 0, 1);

    while (isAfter(current, startLimit) || format(current, "yyyy-MM-dd") === format(startOfWeek(startLimit, { weekStartsOn: 1 }), "yyyy-MM-dd")) {
      const end = addDays(current, 6); // Mon to Sun
      const label = `${format(current, "dd-MMM")} — ${format(end, "dd-MMM yyyy")}`;
      options.push({ value: current.toISOString(), label });
      current = subWeeks(current, 1);
    }
    return options;
  }, []);

  const weekDays = useMemo(() => {
    if (!selectedWeekStart) return [];
    return Array.from({ length: 7 }, (_, i) => {
      const d = addDays(selectedWeekStart, i);
      return format(d, "yyyy-MM-dd");
    });
  }, [selectedWeekStart]);

  /* ------------------- Filtering State ------------------- */
  const [filterType, setFilterType] = useState<"all" | "week" | "range">("all");
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - (d.getDay() === 0 ? 6 : d.getDay() - 1)); // start of current week
    return formatDateYYYYMMDD(d);
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    const friday = new Date(d);
    friday.setDate(d.getDate() + (6 - (d.getDay() === 0 ? 7 : d.getDay()))); // end of current week (Sat)
    return formatDateYYYYMMDD(friday);
  });

  // Helper for internal state dates
  function formatDateYYYYMMDD(date: Date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  const me = users.find((u) => u.id === currentUserId);
  const tsPerms = getTimesheetPermissions(me);

  // Admin sees all; managers/approvers with approvalStatus see only their assigned entries
  const isAdmin = tsPerms.isAdmin;
  const isManagerOrMD = (me?.approvalStatus || "").toLowerCase() === "yes" || (me?.role || "").toLowerCase() === "manager" || (me?.role || "").toLowerCase() === "managingdirector";

  useEffect(() => {
    setLoading(true);
    const fetchAllPending = async () => {
      try {
        const byUser: Record<string, TimesheetEntry[]> = {};

        // Fetch from each user's collection
        await Promise.all(users.map(async (user) => {
          const userId = user.id;
          const seenKeys = new Set<string>();
          const allUserEntries: any[] = [];

          // 1. Weekly architecture: TimeSheetsData/{userId}/weeks/{weekMonday}
          const tsColRef = collection(db, "organization", organizationId, "TimeSheetsData", userId, "weeks");
          const snap = await getDocs(tsColRef);
          snap.docs.forEach(wkDoc => {
            const wkData = wkDoc.data();
            const entries = wkData.entries || [];
            entries.forEach((entry: any) => {
              const fullEntry = { ...entry, userId };
              allUserEntries.push(fullEntry);
              const key = `${entry.date}-${entry.project}-${entry.hours}-${entry.timestamp}`;
              seenKeys.add(key);
            });
          });

          // 2. Legacy path
          const oldRef = doc(db, "organization", organizationId, "timesheets", userId);
          const oldSnap = await getDoc(oldRef);
          if (oldSnap.exists()) {
            const legacyEntries = oldSnap.data().entries || [];
            legacyEntries.forEach((e: any, idx: number) => {
              const key = `${e.date}-${e.project}-${e.hours}-${e.timestamp}`;
              if (!seenKeys.has(key)) {
                allUserEntries.push({ ...e, id: `legacy-${userId}-${idx}`, userId });
              }
            });
          }

          allUserEntries.forEach(entry => {
            const status = (entry.status || "").toLowerCase();
            if (status !== "pending") return;

            // Apply Weekly Filter
            if (selectedWeekStartStr !== "ALL") {
              if (!weekDays.includes(entry.date)) return;
            }

            // Prevent self-approval
            if (userId === currentUserId) return;

            const hasManagerPermission = (Array.isArray(entry.managerIds) && entry.managerIds.includes(currentUserId));
            const shouldInclude = isAdmin || hasManagerPermission;

            if (!shouldInclude) return;

            if (!byUser[userId]) byUser[userId] = [];
            byUser[userId].push({ ...entry, userId });
          });
        }));

        // Sort each user's entries by date/timestamp
        Object.keys(byUser).forEach(uid => {
          byUser[uid].sort((a: any, b: any) => {
            const dateA = new Date(a.date).getTime();
            const dateB = new Date(b.date).getTime();
            if (dateA !== dateB) return dateB - dateA;
            return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
          });
        });

        setEntriesByUser(byUser);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching approvals:", err);
        setLoading(false);
      }
    };

    fetchAllPending();
  }, [organizationId, currentUserId, isManagerOrMD, users, selectedWeekStartStr, weekDays]);

  // Approve/Reject all for current scope: specific user or ALL users
  const updateAllStatus = async (newStatus: "approved" | "rejected") => {
    const targetUserIds = selectedUser === "ALL" ? Object.keys(entriesByUser) : [selectedUser];

    try {
      // Group entries by userId and path type for efficient batch updates
      const legacyByUser: Record<string, { entries: TimesheetEntry[]; indices: number[] }> = {};
      const newByUser: Record<string, TimesheetEntry[]> = {};

      await Promise.all(targetUserIds.map(async (uid) => {
        const entries = entriesByUser[uid] || [];
        entries.forEach(entry => {
          if (entry.id.startsWith("legacy-")) {
            const idx = parseInt(entry.id.split("-").pop()!, 10);
            if (!legacyByUser[uid]) legacyByUser[uid] = { entries: [], indices: [] };
            legacyByUser[uid].entries.push(entry);
            legacyByUser[uid].indices.push(idx);
          } else {
            if (!newByUser[uid]) newByUser[uid] = [];
            newByUser[uid].push(entry);
          }
        });
      }));

      // Update legacy entries (batch per user)
      await Promise.all(Object.entries(legacyByUser).map(async ([uid, { entries: _entries, indices }]) => {
        const oldRef = doc(db, "organization", organizationId, "timesheets", uid);
        const oldSnap = await getDoc(oldRef);
        if (!oldSnap.exists()) return;
        const data = oldSnap.data();
        const allEntries = [...(data.entries || [])];
        indices.forEach(idx => {
          if (idx >= 0 && idx < allEntries.length) {
            allEntries[idx] = { ...allEntries[idx], status: newStatus };
          }
        });
        await updateDoc(oldRef, { entries: allEntries });
      }));

      // Update weekly architecture entries
      await Promise.all(Object.entries(newByUser).map(async ([uid, entries]) => {
        const tsColRef = collection(db, "organization", organizationId, "TimeSheetsData", uid, "weeks");
        const snap = await getDocs(tsColRef);
        for (const wkDoc of snap.docs) {
          const wkData = wkDoc.data();
          const wkEntries = wkData.entries || [];
          let changed = false;
          entries.forEach(entry => {
            const eIdx = wkEntries.findIndex((e: any) => e.id === entry.id);
            if (eIdx !== -1) {
              wkEntries[eIdx] = { ...wkEntries[eIdx], status: newStatus };
              changed = true;
            }
          });
          if (changed) {
            await setDoc(wkDoc.ref, { ...wkData, entries: wkEntries }, { merge: true });
          }
        }
      }));

      // Clear local state for target users
      setEntriesByUser(prev => {
        const next = { ...prev };
        targetUserIds.forEach(uid => delete next[uid]);
        return next;
      });

      setRowComments({});
      setNotification(`All filtered timesheets ${newStatus === "approved" ? "approved" : "rejected"}.`);
      setNotificationType(newStatus === "approved" ? "success" : "error");
      setTimeout(() => { setNotification(null); setNotificationType(null); }, 3000);
    } catch (err) {
      console.error("Batch update failed:", err);
      setNotification("Failed to update some entries.");
      setNotificationType("error");
    }
  };

  // Per-user bulk for grouped sections  
  const updateAllForUser = async (uid: string, newStatus: "approved" | "rejected") => {
    try {
      const entries = entriesByUser[uid] || [];
      const legacyIndices: number[] = [];
      const newEntries: TimesheetEntry[] = [];

      entries.forEach(entry => {
        if (entry.id.startsWith("legacy-")) {
          legacyIndices.push(parseInt(entry.id.split("-").pop()!, 10));
        } else {
          newEntries.push(entry);
        }
      });

      // Update legacy entries in the flat array
      if (legacyIndices.length > 0) {
        const oldRef = doc(db, "organization", organizationId, "timesheets", uid);
        const oldSnap = await getDoc(oldRef);
        if (oldSnap.exists()) {
          const data = oldSnap.data();
          const allEntries = [...(data.entries || [])];
          legacyIndices.forEach(idx => {
            if (idx >= 0 && idx < allEntries.length) {
              const comment = rowComments[`${uid}-legacy-${uid}-${idx}`] || "";
              allEntries[idx] = { ...allEntries[idx], status: newStatus, rejectionReason: comment || allEntries[idx].rejectionReason };
            }
          });
          await updateDoc(oldRef, { entries: allEntries });
        }
      }

      // Update weekly architecture entries
      if (newEntries.length > 0) {
        const tsColRef = collection(db, "organization", organizationId, "TimeSheetsData", uid, "weeks");
        const snap = await getDocs(tsColRef);
        for (const wkDoc of snap.docs) {
          const wkData = wkDoc.data();
          const wkEntries = wkData.entries || [];
          let changed = false;
          newEntries.forEach(entry => {
            const eIdx = wkEntries.findIndex((e: any) => e.id === entry.id);
            if (eIdx !== -1) {
              const comment = rowComments[`${uid}-${entry.id}`] || "";
              wkEntries[eIdx] = { ...wkEntries[eIdx], status: newStatus, rejectionReason: comment || wkEntries[eIdx].rejectionReason };
              changed = true;
            }
          });
          if (changed) {
            await setDoc(wkDoc.ref, { ...wkData, entries: wkEntries }, { merge: true });
          }
        }
      }

      // Clear local state for this user
      setEntriesByUser(prev => {
        const next = { ...prev };
        delete next[uid];
        return next;
      });

      setNotification(`${newStatus === "approved" ? "Approved" : "Rejected"} all entries for this user.`);
      setNotificationType(newStatus === "approved" ? "success" : "error");
      setTimeout(() => { setNotification(null); setNotificationType(null); }, 3000);
    } catch (err) {
      console.error("User batch update failed:", err);
    }
  };

  // UI helpers: confirm dialogs
  const confirmAndUpdateAll = async (newStatus: "approved" | "rejected") => {
    const scope = selectedUser === "ALL" ? "all users" : (() => {
      const u = users.find(uu => uu.id === selectedUser);
      return u ? `${u.firstName} ${u.lastName}` : selectedUser;
    })();
    const actionWord = newStatus === "approved" ? "approve" : "reject";
    setConfirmState({
      open: true,
      title: `Confirm ${newStatus === "approved" ? "Approval" : "Rejection"}`,
      description: `Are you sure you want to ${actionWord} all pending timesheets for ${scope}?`,
      confirmText: `${newStatus === "approved" ? "Approve" : "Reject"} All`,
      cancelText: "Cancel",
      onConfirm: async () => {
        await updateAllStatus(newStatus);
      },
    });
  };

  const confirmAndUpdateAllForUser = async (uid: string, newStatus: "approved" | "rejected") => {
    const u = users.find(uu => uu.id === uid);
    const name = u ? `${u.firstName} ${u.lastName}` : uid;
    const actionWord = newStatus === "approved" ? "approve" : "reject";
    setConfirmState({
      open: true,
      title: `Confirm ${newStatus === "approved" ? "Approval" : "Rejection"}`,
      description: `Are you sure you want to ${actionWord} all pending timesheets for ${name}?`,
      confirmText: `${newStatus === "approved" ? "Approve" : "Reject"} All`,
      cancelText: "Cancel",
      onConfirm: async () => {
        await updateAllForUser(uid, newStatus);
      },
    });
  };

  // All users for dropdown
  // Only show users who have timesheet entries in the dropdown
  const userOptions = users.filter(user => entriesByUser[user.id] && entriesByUser[user.id].length > 0);
  const displayedEntriesBase = selectedUser === "ALL"
    ? Object.values(entriesByUser).flat()
    : (entriesByUser[selectedUser] || []);
  const displayedEntries = displayedEntriesBase.filter(e => e.project?.toLowerCase().includes(search.toLowerCase()));
  const entriesByUserFiltered: Record<string, TimesheetEntry[]> = Object.fromEntries(
    Object.entries(entriesByUser).map(([uid, items]) => [
      uid,
      (items || []).filter(e => e.project?.toLowerCase().includes(search.toLowerCase()))
    ])
  );
  const selectedUserObj = users.find((u) => u.id === selectedUser);
  const singleUserManagerIds = displayedEntries[0]?.managerIds || [];
  const singleUserManagerNames = Array.isArray(singleUserManagerIds)
    ? singleUserManagerIds
      .map((mid) => {
        const mu = users.find((u) => u.id === mid);
        return mu ? `${mu.firstName} ${mu.lastName}`.trim() : null;
      })
      .filter(Boolean)
      .join(", ")
    : "";

  useEffect(() => {
    setSearch(""); // Reset search on user change
  }, [selectedUser]);

  // Initialize selected user and display name when users prop is available
  useEffect(() => {
    if (!selectedUser && users && users.length > 0) {
      setSelectedUser(users[0].id);
    }
  }, [users]);

  // Close user picker on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-user-picker]')) {
        setIsUserPickerOpen(false);
      }
    };
    if (isUserPickerOpen) {
      document.addEventListener('mousedown', onClick);
      return () => document.removeEventListener('mousedown', onClick);
    }
  }, [isUserPickerOpen]);

  // Decide dropdown placement
  useEffect(() => {
    const recomputePlacement = () => {
      const el = userPickerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const desiredHeight = 240;
      if (spaceBelow < desiredHeight && spaceAbove > spaceBelow) {
        setUserPickerPlacement('top');
      } else {
        setUserPickerPlacement('bottom');
      }
    };
    if (isUserPickerOpen) {
      recomputePlacement();
      window.addEventListener('resize', recomputePlacement);
      window.addEventListener('scroll', recomputePlacement, true);
      return () => {
        window.removeEventListener('resize', recomputePlacement);
        window.removeEventListener('scroll', recomputePlacement, true);
      };
    }
  }, [isUserPickerOpen]);

  // Custom SVG clipboard-clock icon
  const ClipboardClockIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none" {...props}>
      <rect
        x="5"
        y="4"
        width="14"
        height="16"
        rx="2"
        stroke="#9333ea"
        strokeWidth="2"
        fill="none"
      />
      <rect
        x="9"
        y="2"
        width="6"
        height="4"
        rx="1"
        stroke="#9333ea"
        strokeWidth="2"
        fill="none"
      />
      <circle
        cx="12"
        cy="14"
        r="4"
        stroke="#9333ea"
        strokeWidth="2"
        fill="none"
      />
      <path
        d="M12 12v2l1.5 1.5"
        stroke="#9333ea"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );

  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);

  return (
    <div className="w-full h-full flex flex-col py-0 px-4 min-h-0">
      {/* Header */}
      <div className={`flex justify-between items-center p-4 min-h-[64px] overflow-visible ${theme.headerGradient} rounded-md mb-4 border ${theme.border} flex-shrink-0`}>
        {isDark && (
          <>
            <span className={theme.headerOverlayGlow} />
            <span className={theme.headerOverlayTopLine} />
            <span className={theme.headerOverlayBottomGlow} />
            <span className={theme.headerOverlaySweep} />
          </>
        )}
        <div className="flex items-center gap-3">
          <div className="flex flex-col gap-0">
            <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Approvals
            </span>
            <span className={`text-xs ${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>
              Review and approve pending timesheet entries to keep the team on track.
            </span>
          </div>
        </div>
        {/* Tab Switcher — rightmost end of header */}
        {setActiveSubTab && (
          <div
            role="tablist"
            aria-label="Approval type"
            className={`relative grid grid-cols-4 ${isDark ? 'bg-[#14161B] border-[#2E2F33]' : 'bg-white/80 border-gray-200'
              } border rounded-full p-1.5 shadow-sm`}
          >
            <span
              className={`absolute top-1.5 bottom-1.5 rounded-full transition-all duration-300 ${isDark ? 'bg-[#2A3441]' : 'bg-[#FFF7E6] border border-[#E8C16C]'
                } shadow-sm`}
              style={
                activeSubTab === 'timesheets'
                  ? { left: '0.375rem', right: '75%' }
                  : activeSubTab === 'leaves'
                  ? { left: '25%', right: '50%' }
                  : activeSubTab === 'wfh'
                  ? { left: '50%', right: '25%' }
                  : { left: '75%', right: '0.375rem' }
              }
              aria-hidden="true"
            />
            <button
              role="tab"
              aria-selected={activeSubTab === 'timesheets'}
              onClick={() => setActiveSubTab('timesheets')}
              className={`relative z-10 flex items-center justify-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors focus:outline-none whitespace-nowrap ${activeSubTab === 'timesheets'
                ? isDark ? 'text-white' : 'text-gray-900'
                : isDark ? 'text-[#9CA3AF] hover:text-[#B59143]' : 'text-gray-500 hover:text-[#B59143]'
                }`}
            >
              Timesheets
            </button>
            <button
              role="tab"
              aria-selected={activeSubTab === 'leaves'}
              onClick={() => setActiveSubTab('leaves')}
              className={`relative z-10 flex items-center justify-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors focus:outline-none whitespace-nowrap ${activeSubTab === 'leaves'
                ? isDark ? 'text-white' : 'text-gray-900'
                : isDark ? 'text-[#9CA3AF] hover:text-[#B59143]' : 'text-gray-500 hover:text-[#B59143]'
                }`}
            >
              Leaves
            </button>
            <button
              role="tab"
              aria-selected={activeSubTab === 'wfh'}
              onClick={() => setActiveSubTab('wfh')}
              className={`relative z-10 flex items-center justify-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors focus:outline-none whitespace-nowrap ${activeSubTab === 'wfh'
                ? isDark ? 'text-white' : 'text-gray-900'
                : isDark ? 'text-[#9CA3AF] hover:text-[#B59143]' : 'text-gray-500 hover:text-[#B59143]'
                }`}
            >
              Remote Work
            </button>
            <button
              role="tab"
              aria-selected={activeSubTab === 'noncheckin'}
              onClick={() => setActiveSubTab('noncheckin')}
              className={`relative z-10 flex items-center justify-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors focus:outline-none whitespace-nowrap ${activeSubTab === 'noncheckin'
                ? isDark ? 'text-white' : 'text-gray-900'
                : isDark ? 'text-[#9CA3AF] hover:text-[#B59143]' : 'text-gray-500 hover:text-[#B59143]'
                }`}
            >
              Non Checkins
            </button>
          </div>
        )}
      </div>

      {/* Content Container - separate from header like LeaveApprovalPage */}
      <div className={`${isDark ? 'border border-[#2E2F33] bg-[#1A1C1F] text-white' : 'border border-gray-200 bg-white'} rounded-md max-w-full flex-1 min-h-0 flex flex-col`}>

        {/* Pending Timesheets Heading */}
        {selectedUser !== "ALL" && selectedUserObj && (
          <div className="mb-3 flex flex-col gap-1 px-6 pt-4">
            <span className="text-md font-semibold">
              Pending Timesheets for {selectedUserObj.firstName} {selectedUserObj.lastName}
            </span>
            {singleUserManagerNames && (
              <span className={`text-xs font-normal ${isDark ? 'text-[#C3C9D3]' : 'text-gray-500'}`}>
                Managers: {singleUserManagerNames}
              </span>
            )}
          </div>
        )}

        {/* Global Controls */}
        <div className={`p-4 mx-6 border-b ${isDark ? 'border-[#2E2F33] bg-[#1A1C1F]' : 'border-gray-200 bg-white'} flex flex-wrap items-center justify-between gap-4 sticky top-0 z-40 rounded-t-lg shadow-sm mb-4`}>
          <div className="flex items-center gap-2">
            <button
              className={`h-8 px-3 rounded-md text-xs font-bold flex items-center gap-1.5 border transition-all ${displayedEntries.length === 0
                ? isDark ? "border-[#2E2F33] text-gray-600 bg-transparent" : "border-gray-200 text-gray-400 bg-gray-50"
                : isDark ? "border-[#E8C16C] text-[#E8C16C] bg-[#2A2418] hover:bg-[#3D3422]" : "border-[#E8C16C] text-[#B59143] bg-[#FFF7E6] hover:bg-[#FFEFD1]"
                }`}
              onClick={() => confirmAndUpdateAll("approved")}
              disabled={displayedEntries.length === 0}
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              Approve All
            </button>
            <button
              className={`h-8 px-3 rounded-md text-xs font-bold flex items-center gap-1.5 border transition-all ${displayedEntries.length === 0
                ? isDark ? "border-[#2E2F33] text-gray-600 bg-transparent" : "border-gray-200 text-gray-400 bg-gray-50"
                : isDark ? "border-red-500/50 text-red-500 bg-red-900/20 hover:bg-red-900/30" : "border-red-300 text-red-600 bg-red-50 hover:bg-red-100"
                }`}
              onClick={() => confirmAndUpdateAll("rejected")}
              disabled={displayedEntries.length === 0}
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" /></svg>
              Reject All
            </button>
          </div>

          <div className="flex items-center gap-3 flex-1 justify-end">
            {/* Searchable User Picker */}
            <div className="relative flex-shrink-0 w-48" data-user-picker ref={userPickerRef}>
              <button
                type="button"
                onClick={() => { setIsUserPickerOpen(o => !o); setUserQuery(''); }}
                className={`w-full h-8 flex items-center gap-2 rounded-md border ${isDark ? 'border-[#2E2F33] bg-[#14161B] text-white' : 'border-gray-300 bg-white text-gray-800'} px-2.5 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#B59143] text-left transition-all hover:border-[#B59143]`}
              >
                <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                <span className={`flex-1 text-xs truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {selectedUser === 'ALL' ? 'All Users' : (() => {
                    const u = users.find(u => u.id === selectedUser);
                    return u ? `${u.firstName} ${u.lastName}` : selectedUser;
                  })()}
                </span>
                <svg className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${isUserPickerOpen ? 'rotate-180' : 'rotate-0'}`} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" /></svg>
              </button>
              {isUserPickerOpen && (
                <div
                  className={`fixed z-[9999] w-48 rounded-lg border ${isDark ? 'border-[#2E2F33] bg-[#1A1C1F]' : 'border-gray-200 bg-white'} shadow-xl`}
                  style={(() => {
                    const btn = userPickerRef.current?.querySelector('button');
                    if (!btn) return { top: 0, left: 0 };
                    const r = btn.getBoundingClientRect();
                    return { top: r.bottom + 4, left: r.left, width: r.width };
                  })()}
                >
                  <div className={`sticky top-0 ${isDark ? 'bg-[#1A1C1F] border-[#2E2F33]' : 'bg-white border-gray-100'} border-b p-2`}>
                    <input
                      autoFocus
                      value={userQuery}
                      onChange={(e) => setUserQuery(e.target.value)}
                      placeholder="Search user..."
                      className={`w-full rounded-md border ${isDark ? 'border-[#2E2F33] bg-[#14161B] text-white placeholder-gray-500' : 'border-gray-300 bg-white text-gray-900'} px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#B59143]`}
                    />
                  </div>
                  <div className="max-h-60 overflow-auto py-1">
                    <button
                      className={`w-full text-left px-3 py-2 text-sm transition-colors ${selectedUser === 'ALL' ? isDark ? 'bg-[#2A2418] text-[#E8C16C]' : 'bg-[#FFF7E6] text-[#B59143]' : isDark ? 'text-gray-200 hover:bg-[#2E2F33]' : 'text-gray-800 hover:bg-gray-50'}`}
                      onClick={() => { setSelectedUser('ALL'); setIsUserPickerOpen(false); }}
                    >
                      All Users
                    </button>
                    {userOptions
                      .filter(u => `${u.firstName} ${u.lastName}`.toLowerCase().includes(userQuery.toLowerCase()))
                      .map(u => (
                        <button
                          key={u.id}
                          className={`w-full text-left px-3 py-2 text-sm transition-colors ${selectedUser === u.id ? isDark ? 'bg-[#2A2418] text-[#E8C16C]' : 'bg-[#FFF7E6] text-[#B59143]' : isDark ? 'text-gray-200 hover:bg-[#2E2F33]' : 'text-gray-800 hover:bg-gray-50'}`}
                          onClick={() => { setSelectedUser(u.id); setIsUserPickerOpen(false); }}
                        >
                          {u.firstName} {u.lastName}
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>

            <div className="relative w-full max-w-xs">
              <input
                type="text"
                className={`w-full pl-3 pr-8 h-8 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#B59143] transition-all hover:border-[#B59143] ${isDark ? 'border-[#2E2F33] bg-[#14161B] text-white text-xs' : 'border-gray-300 bg-white text-xs'}`}
                placeholder="Search project name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button aria-label="Clear search" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-xs" onClick={() => setSearch("")}>×</button>
              )}
            </div>

            <div className="w-[300px]">
              <CustomDropdown
                value={selectedWeekStartStr}
                onChange={(val) => setSelectedWeekStartStr(val)}
                isDark={isDark}
                placeholder="Select Week"
                className="h-8 text-xs w-full"
                options={generateWeekOptions()}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {loading ? (
            <div className="px-6 pb-4">
              <div className={`rounded-md overflow-hidden border ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'}`}>
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse text-sm table-fixed mb-4">
                    <thead className={`${isDark ? 'bg-[#14161B]' : 'bg-gray-100'}`}>
                      <tr className="h-11">
                        <th className="border border-gray-300 px-2 py-3 text-center w-20 whitespace-nowrap text-xs"><Skeleton h="16px" w="30px" m="auto" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></th>
                        <th className="border border-gray-300 px-4 py-3 text-left w-28 whitespace-nowrap text-xs"><Skeleton h="16px" w="45px" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></th>
                        <th className="border border-gray-300 px-4 py-3 text-left w-[18%] text-xs"><Skeleton h="16px" w="60px" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></th>
                        <th className="border border-gray-300 px-4 py-3 text-left w-[18%] text-xs"><Skeleton h="16px" w="60px" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></th>
                        <th className="border border-gray-300 px-4 py-3 text-left w-[22%] text-xs"><Skeleton h="16px" w="90px" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></th>
                        <th className="border border-gray-300 px-2 py-3 text-center w-20 text-xs"><Skeleton h="16px" w="40px" m="auto" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></th>
                        <th className="border border-gray-300 px-2 py-3 text-center w-20 text-xs"><Skeleton h="16px" w="40px" m="auto" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></th>
                        <th className="border border-gray-300 px-4 py-3 text-left w-[18%] text-xs"><Skeleton h="16px" w="60px" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></th>
                      </tr>
                    </thead>
                    <tbody>
                      {[1, 2, 3, 4, 5].map((rowIdx) => (
                        <tr key={rowIdx} className={`${isDark ? 'bg-[#1A1C1F]' : 'bg-white'}`}>
                          <td className="border border-gray-300 px-2 py-2 text-center text-xs"><Skeleton h="12px" w="20px" m="auto" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
                          <td className="border border-gray-300 px-4 py-2 text-left text-xs"><Skeleton h="12px" w="50px" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
                          <td className="border border-gray-300 px-4 py-2 text-left text-xs"><Skeleton h="12px" w="80px" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
                          <td className="border border-gray-300 px-4 py-2 text-left text-xs"><Skeleton h="12px" w="80px" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
                          <td className="border border-gray-300 px-4 py-2 text-left text-xs"><Skeleton h="12px" w="120px" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
                          <td className="border border-gray-300 px-2 py-2 text-center text-xs"><Skeleton h="12px" w="30px" m="auto" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
                          <td className="border border-gray-300 px-2 py-2 text-center text-xs"><Skeleton h="12px" w="30px" m="auto" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
                          <td className="border border-gray-300 px-2 py-1"><Skeleton h="24px" w="100%" startColor={isDark ? "gray.700" : "gray.200"} endColor={isDark ? "gray.600" : "gray.300"} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : displayedEntries.length === 0 ? (
            <div className={`flex justify-center items-center h-[50vh] ${isDark ? 'text-white' : 'text-gray-700'} p-6`}>
              {search
                ? `No projects found for "${search}".`
                : (selectedUser === 'ALL'
                  ? (selectedWeekStartStr === 'ALL' ? 'No pending timesheets available.' : 'No pending timesheets available for this week.')
                  : `No pending timesheets for ${selectedUserObj ? `${selectedUserObj.firstName} ${selectedUserObj.lastName}` : selectedUser}.`
                )
              }
            </div>
          ) : selectedUser === "ALL" ? (
            <div className="px-6 pb-4 space-y-6">
              {Object.keys(entriesByUser).map((uid) => {
                const user = users.find((u) => u.id === uid);
                const name = user ? `${user.firstName} ${user.lastName}` : uid;
                const items = entriesByUserFiltered[uid] || [];
                if (items.length === 0) return null;

                const userManagerIds = items[0]?.managerIds || [];
                const managerNames = Array.isArray(userManagerIds)
                  ? userManagerIds
                    .map((mid) => {
                      const mu = users.find((u) => u.id === mid);
                      return mu ? `${mu.firstName} ${mu.lastName}`.trim() : null;
                    })
                    .filter(Boolean)
                    .join(", ")
                  : "";

                return (
                  <div key={uid} className={`rounded-md overflow-hidden border ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'}`}>
                    <div className={`sticky top-0 z-30 flex items-center justify-between px-4 py-2 ${isDark ? 'bg-[#14161B]' : 'bg-gray-50'} border-b ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'}`}>
                      <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'} flex flex-col gap-0.5`}>
                        <span>{name}</span>
                        {managerNames && (
                          <span className={`text-xs font-normal ${isDark ? 'text-[#C3C9D3]' : 'text-gray-500'}`}>
                            Manager: {managerNames}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          className="h-8 px-3 rounded-md text-xs flex items-center gap-1 border border-[#E8C16C] text-[#B59143] hover:bg-[#FFF7E6]"
                          onClick={() => confirmAndUpdateAllForUser(uid, "approved")}
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                          Approve All
                        </button>
                        <button
                          className="h-8 px-3 rounded-md text-xs flex items-center gap-1 border border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => confirmAndUpdateAllForUser(uid, "rejected")}
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" /></svg>
                          Reject All
                        </button>
                      </div>
                    </div>
                    <div className="overflow-x-auto max-h-[350px] overflow-y-auto relative">
                      <table className="min-w-full border-collapse text-sm table-fixed mb-4">
                        <thead className={`sticky top-0 z-20 ${isDark ? 'bg-[#14161B]' : 'bg-gray-100'} border-b border-gray-300`}>
                          <tr className="h-11">
                            <th className="border border-gray-300 px-2 py-2 text-center font-bold text-black w-20 whitespace-nowrap text-xs">S No</th>
                            <th className="border border-gray-300 px-4 py-2 text-left font-bold text-black w-28 whitespace-nowrap text-xs">Date</th>
                            <th className="border border-gray-300 px-4 py-2 text-left font-bold text-gray-700 w-[18%] text-xs">Project</th>
                            <th className="border border-gray-300 px-4 py-2 text-left font-bold text-gray-700 w-[18%] text-xs">Channel</th>
                            <th className="border border-gray-300 px-4 py-2 text-left font-bold text-gray-700 w-[22%] text-xs">Description</th>
                            <th className="border border-gray-300 px-2 py-2 text-center font-bold text-gray-700 w-20 text-xs">Hours</th>
                            <th className="border border-gray-300 px-2 py-2 text-center font-bold text-black w-20 text-xs">Total</th>
                            <th className="border border-gray-300 px-4 py-2 text-left font-bold text-gray-700 w-[18%] text-xs">Comments</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const groupedByDate: Record<string, TimesheetEntry[]> = {};
                            items.forEach(e => {
                              const d = e.date;
                              if (!groupedByDate[d]) groupedByDate[d] = [];
                              groupedByDate[d].push(e);
                            });
                            const sortedDates = Object.keys(groupedByDate).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
                            let sNoCounter = 1;

                            return sortedDates.map((dateStr) => {
                              const dateEntries = groupedByDate[dateStr];
                              const dailyTotal = dateEntries.reduce((sum, e) => sum + (parseFloat(e.hours?.toString() || "0") || 0), 0);
                              const currentSNo = sNoCounter++;

                              return dateEntries.map((e, idx) => (
                                <tr key={`${uid}-${e.id}`} className={`${isDark ? 'bg-[#1A1C1F] hover:bg-[#2A3441]' : 'bg-white hover:bg-gray-50'}`}>
                                  {idx === 0 && (
                                    <>
                                      <td rowSpan={dateEntries.length} className="border border-gray-300 px-2 py-2 text-center font-normal text-black align-middle whitespace-nowrap text-xs">
                                        {currentSNo}
                                      </td>
                                      <td rowSpan={dateEntries.length} className="border border-gray-300 px-4 py-2 text-left font-normal text-black align-middle whitespace-nowrap text-xs">
                                        {new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                      </td>
                                    </>
                                  )}
                                  <td className="border border-gray-300 px-4 py-2 text-left font-normal text-xs leading-tight whitespace-pre-wrap">{e.project}</td>
                                  <td className="border border-gray-300 px-4 py-2 text-left font-normal text-xs leading-tight whitespace-pre-wrap">{e.jobCode}</td>
                                  <td className="border border-gray-300 px-4 py-2 text-left font-normal text-xs leading-tight whitespace-pre-wrap">{e.taskDescription || "-"}</td>
                                  <td className="border border-gray-300 px-2 py-2 text-center font-normal text-xs">{e.hours}</td>
                                  {idx === 0 && (
                                    <td rowSpan={dateEntries.length} className="border border-gray-300 px-2 py-2 text-center font-bold text-black align-middle">
                                      {dailyTotal}
                                    </td>
                                  )}
                                  <td className="border border-gray-300 px-2 py-1">
                                    <textarea
                                      placeholder="Add comment..."
                                      rows={1}
                                      className={`w-full px-2 py-1 text-xs rounded outline-none transition-all focus:ring-1 focus:ring-[#B59143] resize-none overflow-hidden whitespace-pre-wrap min-h-[24px] ${isDark
                                        ? 'bg-transparent border-transparent hover:bg-[#14161B] hover:border-[#2E2F33] focus:bg-[#14161B] focus:border-[#2E2F33] text-white placeholder-gray-600'
                                        : 'bg-transparent border-transparent hover:bg-gray-50 hover:border-gray-300 focus:bg-white focus:border-gray-300 text-gray-900 placeholder-gray-400'}`}
                                      value={rowComments[`${uid}-${e.id}`] || ""}
                                      onChange={(event) => {
                                        setRowComments(prev => ({ ...prev, [`${uid}-${e.id}`]: event.target.value }));
                                        event.target.style.height = 'auto';
                                        event.target.style.height = `${event.target.scrollHeight}px`;
                                      }}
                                      onFocus={(e) => {
                                        e.target.style.height = 'auto';
                                        e.target.style.height = `${e.target.scrollHeight}px`;
                                      }}
                                    />
                                  </td>
                                </tr>
                              ));
                            });
                          })()}
                        </tbody>
                        <tfoot className={`${isDark ? 'bg-[#14161B]' : 'bg-gray-100'} border-t border-gray-300`}>
                          <tr className="h-10">
                            <td colSpan={6} className={`border border-gray-300 px-4 py-2 text-right font-bold ${isDark ? 'text-white' : 'text-black'} uppercase text-[10px]`}>Grand Total:</td>
                            <td className={`border border-gray-300 px-2 py-2 text-center font-black text-sm ${isDark ? 'text-white' : 'text-black'} ${isDark ? 'bg-[#14161B]' : 'bg-gray-100'}`}>
                              {items.reduce((sum, e) => sum + (parseFloat(e.hours?.toString() || "0") || 0), 0).toFixed(1)}
                            </td>
                            <td colSpan={1} className={`border border-gray-300 ${isDark ? 'bg-[#14161B]' : 'bg-gray-100'}`}></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="overflow-x-auto relative px-6">
              <table className="min-w-full border-collapse text-sm table-fixed mb-4">
                <thead className={`sticky top-0 z-20 ${isDark ? 'bg-[#14161B]' : 'bg-gray-100'} border-b border-gray-300`}>
                  <tr className="h-11 vertical-middle">
                    <th className="border border-gray-300 px-2 py-2 text-center font-bold text-black w-20 whitespace-nowrap text-xs">S No</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-bold text-black w-28 whitespace-nowrap text-xs">Date</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-bold text-gray-700 w-1/5 text-xs">Project</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-bold text-gray-700 w-1/5 text-xs">Channel</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-bold text-gray-700 w-1/4 text-xs">Description</th>
                    <th className="border border-gray-300 px-2 py-2 text-center font-bold text-gray-700 w-16 text-xs">Hours</th>
                    <th className="border border-gray-300 px-2 py-2 text-center font-bold text-black w-14 text-xs">Total</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-bold text-gray-700 w-1/4 text-xs">Comments</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const groupedByDate: Record<string, TimesheetEntry[]> = {};
                    displayedEntries.forEach(e => {
                      const d = e.date;
                      if (!groupedByDate[d]) groupedByDate[d] = [];
                      groupedByDate[d].push(e);
                    });
                    const sortedDates = Object.keys(groupedByDate).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
                    let sNoCounter = 1;

                    return sortedDates.map((dateStr) => {
                      const dateEntries = groupedByDate[dateStr];
                      const dailyTotal = dateEntries.reduce((sum, e) => sum + (e.hours || 0), 0);
                      const currentSNo = sNoCounter++;

                      return dateEntries.map((e, idx) => (
                        <tr key={e.id} className={`${isDark ? 'bg-[#1A1C1F] hover:bg-[#2A3441]' : 'bg-white hover:bg-gray-50'}`}>
                          {idx === 0 && (
                            <>
                              <td rowSpan={dateEntries.length} className="border border-gray-300 px-2 py-2 text-center font-normal text-black align-middle whitespace-nowrap text-xs">
                                {currentSNo}
                              </td>
                              <td rowSpan={dateEntries.length} className="border border-gray-300 px-4 py-2 text-left font-normal text-black align-middle whitespace-nowrap text-xs">
                                {new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </td>
                            </>
                          )}
                          <td className="border border-gray-300 px-4 py-2 text-left font-normal text-xs leading-tight whitespace-pre-wrap">{e.project}</td>
                          <td className="border border-gray-300 px-4 py-2 text-left font-normal text-xs leading-tight whitespace-pre-wrap">{e.jobCode}</td>
                          <td className="border border-gray-300 px-4 py-2 text-left text-xs leading-tight whitespace-pre-wrap">{e.taskDescription || "-"}</td>
                          <td className="border border-gray-300 px-2 py-2 text-center text-xs">{e.hours}</td>
                          {idx === 0 && (
                            <td rowSpan={dateEntries.length} className="border border-gray-300 px-2 py-2 text-center font-bold text-black align-middle">
                              {dailyTotal}
                            </td>
                          )}
                          <td className="border border-gray-300 px-2 py-1">
                            <textarea
                              placeholder="Add comment..."
                              rows={1}
                              className={`w-full px-2 py-1 text-xs rounded outline-none transition-all focus:ring-1 focus:ring-[#B59143] resize-none overflow-hidden whitespace-pre-wrap min-h-[24px] ${isDark
                                ? 'bg-transparent border-transparent hover:bg-[#14161B] hover:border-[#2E2F33] focus:bg-[#14161B] focus:border-[#2E2F33] text-white placeholder-gray-600'
                                : 'bg-transparent border-transparent hover:bg-gray-50 hover:border-gray-300 focus:bg-white focus:border-gray-300 text-gray-900 placeholder-gray-400'}`}
                              value={rowComments[`${e.userId}-${e.id}`] || ""}
                              onChange={(event) => {
                                setRowComments(prev => ({ ...prev, [`${e.userId}-${e.id}`]: event.target.value }));
                                event.target.style.height = 'auto';
                                event.target.style.height = `${event.target.scrollHeight}px`;
                              }}
                              onFocus={(e) => {
                                e.target.style.height = 'auto';
                                e.target.style.height = `${e.target.scrollHeight}px`;
                              }}
                            />
                          </td>
                        </tr>
                      ));
                    });
                  })()}
                </tbody>
                <tfoot className={`${isDark ? 'bg-[#14161B]' : 'bg-gray-100'} border-t border-gray-300`}>
                  <tr className="h-10">
                    <td colSpan={6} className={`border border-gray-300 px-4 py-2 text-right font-bold ${isDark ? 'text-white' : 'text-black'} uppercase text-[10px]`}>Grand Total:</td>
                    <td className={`border border-gray-300 px-2 py-2 text-center font-black text-sm ${isDark ? 'text-white' : 'text-black'} ${isDark ? 'bg-[#14161B]' : 'bg-gray-100'}`}>
                      {displayedEntries.reduce((sum, e) => sum + (parseFloat(e.hours.toString()) || 0), 0).toFixed(1)}
                    </td>
                    <td colSpan={1} className={`border border-gray-300 ${isDark ? 'bg-[#14161B]' : 'bg-gray-100'}`}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )
          }
        </div>
      </div>

      {/* Task Tooltip Overlay */}
      {tooltip && (
        <div
          className={`fixed z-[9999] px-3 py-2 rounded-lg shadow-xl text-xs leading-relaxed max-w-xs pointer-events-none border ${isDark
            ? 'bg-[#2A2D35] text-gray-100 border-[#3E4049]'
            : 'bg-white text-gray-800 border-gray-200'
            }`}
          style={{
            left: tooltip.x,
            top: tooltip.y - 8,
            transform: 'translateY(-100%)',
          }}
        >
          {tooltip.text}
          <span className={`absolute top-full left-4 border-[5px] border-transparent ${isDark ? 'border-t-[#3E4049]' : 'border-t-gray-200'}`} style={{ marginTop: '-1px' }} />
          <span className={`absolute top-full left-4 border-[5px] border-transparent ${isDark ? 'border-t-[#2A2D35]' : 'border-t-white'}`} style={{ marginTop: '-2px' }} />
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div
          className={`fixed bottom-4 right-4 px-4 py-2 rounded shadow-lg z-50 animate-fade-in ${notificationType === "success"
            ? "bg-green-600 text-white"
            : "bg-red-600 text-white"
            }`}
        >
          {notification}
        </div>
      )}
      {confirmState?.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setConfirmState(null)} />
          <div className="relative bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-md mx-4 p-5">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">{confirmState.title}</h3>
            {confirmState.description && (
              <p className="text-sm text-gray-600 mb-4">{confirmState.description}</p>
            )}
            <div className="flex justify-end gap-2">
              <button className="px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100" onClick={() => setConfirmState(null)}>
                {confirmState.cancelText || 'Cancel'}
              </button>
              <button
                className="px-3 py-1.5 rounded-md border border-[#E8C16C] text-[#B59143] hover:bg-[#FFF7E6]"
                onClick={async () => { const fn = confirmState.onConfirm; setConfirmState(null); await fn(); }}
              >
                {confirmState.confirmText || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default TimeSheetApprovalPage;
